import { formatElevation, getTicks, parseCssPixels, round, thinPoints } from "./utils.js";
import { interpolatePoint } from "./profile.js";

export function renderProfile(profile, root, chartWrap, chart, handlers) {
  const height = parseCssPixels(root.dataset.profileHeight, 360);
  const margin = { top: 28, right: 26, bottom: 58, left: 58 };
  const containerWidth = Math.max(chartWrap.clientWidth || root.clientWidth || 320, 320);
  const width = containerWidth;
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const range = Math.max(profile.maxElevation - profile.minElevation, 20);
  const minY = profile.minElevation - range * 0.08;
  const maxY = profile.maxElevation + range * 0.12;
  const totalDistance = Math.max(profile.totalDistance, 0.001);
  const elevationPoints = profile.elevationPoints || profile.points;
  const displayPoints = thinPoints(elevationPoints, 900);
  const gradientPrefix = root.id || "bhgpxv";
  const lineGradientId = `${gradientPrefix}-line-gradient`;
  const areaGradientId = `${gradientPrefix}-area-gradient`;

  const x = (distance) => margin.left + (distance / totalDistance) * plotWidth;
  const y = (elevation) => margin.top + ((maxY - elevation) / (maxY - minY)) * plotHeight;
  const linePath = displayPoints.map((point, index) => `${index === 0 ? "M" : "L"}${round(x(point.distance))} ${round(y(point.smoothedElevation))}`).join(" ");
  const areaPath = `${linePath} L ${round(x(profile.totalDistance))} ${height - margin.bottom} L ${margin.left} ${height - margin.bottom} Z`;
  const yTicks = getTicks(minY, maxY, 5);

  chart.innerHTML = `
    <svg class="bhgpxv__svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="${lineGradientId}" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stop-color="#14b8a6" />
          <stop offset="52%" stop-color="#22c55e" />
          <stop offset="100%" stop-color="#0ea5e9" />
        </linearGradient>
        <linearGradient id="${areaGradientId}" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#22c55e" stop-opacity="0.34" />
          <stop offset="62%" stop-color="#5eead4" stop-opacity="0.12" />
          <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
        </linearGradient>
      </defs>
      ${yTicks.map((tick) => `
        <line class="bhgpxv__grid" x1="${margin.left}" x2="${width - margin.right}" y1="${round(y(tick))}" y2="${round(y(tick))}" />
        <text class="bhgpxv__axisLabel" x="${margin.left - 10}" y="${round(y(tick) + 4)}" text-anchor="end">${Math.round(tick)} m</text>
      `).join("")}
      ${profile.kmMarkers.map((marker) => `
        <line class="bhgpxv__km-line" x1="${round(x(marker.distance))}" x2="${round(x(marker.distance))}" y1="${margin.top}" y2="${height - margin.bottom}" />
      `).join("")}
      <rect class="bhgpxv__range" x="${margin.left}" y="${margin.top}" width="0" height="${plotHeight}" hidden />
      <path class="bhgpxv__area" d="${areaPath}" style="fill: url(#${areaGradientId});" />
      <path class="bhgpxv__line" d="${linePath}" style="stroke: url(#${lineGradientId});" />
      ${profile.kmMarkers.map((marker) => `
        <circle class="bhgpxv__km-dot" cx="${round(x(marker.distance))}" cy="${round(y(marker.elevation))}" r="4" />
        <text class="bhgpxv__elevation-label" x="${round(x(marker.distance))}" y="${round(y(marker.elevation) - 10)}" text-anchor="middle">${Math.round(marker.elevation)} m</text>
        <text class="bhgpxv__km-label" x="${round(x(marker.distance))}" y="${height - 24}" text-anchor="middle">${marker.km} km</text>
      `).join("")}
      <line class="bhgpxv__cursorLine" x1="${margin.left}" x2="${margin.left}" y1="${margin.top}" y2="${height - margin.bottom}" hidden />
      <circle class="bhgpxv__cursorDot" cx="${margin.left}" cy="${margin.top}" r="6" hidden />
      <text class="bhgpxv__cursorLabel" x="${margin.left}" y="${margin.top - 10}" text-anchor="middle" hidden>-</text>
      <text class="bhgpxv__axisLabel" x="${width - margin.right}" y="${height - 8}" text-anchor="end">Distance</text>
      <rect class="bhgpxv__hoverTarget" x="${margin.left}" y="${margin.top}" width="${plotWidth}" height="${plotHeight}" tabindex="0" />
    </svg>
  `;
  chartWrap.classList.add("has-chart");

  const svg = chart.querySelector(".bhgpxv__svg");
  const hoverTarget = chart.querySelector(".bhgpxv__hoverTarget");
  const cursorLine = chart.querySelector(".bhgpxv__cursorLine");
  const cursorDot = chart.querySelector(".bhgpxv__cursorDot");
  const cursorLabel = chart.querySelector(".bhgpxv__cursorLabel");
  const rangeEl = chart.querySelector(".bhgpxv__range");

  hoverTarget.addEventListener("mousemove", (event) => {
    handlers.onHover(distanceFromPointer(svg, event, margin.left, plotWidth, totalDistance));
  });
  hoverTarget.addEventListener("mouseleave", handlers.onLeave);
  hoverTarget.addEventListener("focus", () => handlers.onHover(totalDistance / 2));
  hoverTarget.addEventListener("blur", handlers.onLeave);
  hoverTarget.addEventListener("touchmove", (event) => {
    if (!event.touches.length) {
      return;
    }

    handlers.onHover(distanceFromPointer(svg, event.touches[0], margin.left, plotWidth, totalDistance));
  }, { passive: true });
  hoverTarget.addEventListener("touchend", handlers.onLeave);

  return {
    setCursorDistance(distance) {
      const point = interpolatePoint(elevationPoints, distance);
      const cursorX = round(x(point.distance));
      const cursorY = round(y(point.smoothedElevation));

      cursorLine.setAttribute("x1", cursorX);
      cursorLine.setAttribute("x2", cursorX);
      cursorDot.setAttribute("cx", cursorX);
      cursorDot.setAttribute("cy", cursorY);
      cursorLabel.setAttribute("x", cursorX);
      cursorLabel.textContent = formatElevation(point.smoothedElevation);
      setSvgHidden(cursorLine, false);
      setSvgHidden(cursorDot, false);
      setSvgHidden(cursorLabel, false);
    },
    clearCursor() {
      setSvgHidden(cursorLine, true);
      setSvgHidden(cursorDot, true);
      setSvgHidden(cursorLabel, true);
    },
    setRange(startDistance, endDistance) {
      rangeEl.setAttribute("x", round(x(startDistance)));
      rangeEl.setAttribute("width", Math.max(1, round(x(endDistance) - x(startDistance))));
      setSvgHidden(rangeEl, false);
    },
    clearRange() {
      setSvgHidden(rangeEl, true);
    },
  };
}

function distanceFromPointer(svg, event, marginLeft, plotWidth, totalDistance) {
  const rect = svg.getBoundingClientRect();
  const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left - marginLeft) / plotWidth));
  return ratio * totalDistance;
}

function setSvgHidden(element, isHidden) {
  if (isHidden) {
    element.setAttribute("hidden", "");
    return;
  }

  element.removeAttribute("hidden");
}
